import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import Swal from "sweetalert2";

import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import { getCustomSelectStyles } from "../../../Layouts/admin/shared/theme/selectStyles";
import SgTable from "../../sg/components/SgTable";
import type { SgColumn } from "../../sg/components/SgTable";
import {
  fetchArchiveJob,
  fetchArchiveFiles,
  deleteArchiveBackup,
  retryArchiveJob,
  downloadArchiveRange,
  archiveDownloadErrorText,
} from "../../../features/device-archive/jobs";
import type { ArchiveExportFormat } from "../../../features/device-archive/jobs";
import type {
  ArchiveFileEntry,
  ArchiveSource,
} from "../../../features/device-archive/jobs";
import { StatusBadge, ReasonBadge } from "../components/badges";
import { confirmStrongDelete } from "../functions";

const ALL_SOURCES: ArchiveSource[] = [
  "positions",
  "events",
  "commands",
  "geofences",
];

interface Option {
  value: string;
  label: string;
}

export default function JobDetail() {
  const { jobId = "" } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("respaldos");
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);
  const queryClient = useQueryClient();
  const selectStyles = getCustomSelectStyles(isDarkMode);

  const [deviceId, setDeviceId] = useState("");
  const [source, setSource] = useState<ArchiveSource | "">("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    deviceId: "",
    source: "" as ArchiveSource | "",
    from: "",
    to: "",
  });
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<ArchiveExportFormat>("excel");

  const {
    data: job,
    isPending: jobLoading,
    isError: jobError,
    error: jobErr,
  } = useQuery({
    queryKey: ["archive-job", jobId],
    queryFn: () => fetchArchiveJob(jobId),
    enabled: !!jobId,
  });

  const {
    data: files,
    isPending: filesLoading,
    isFetching: filesFetching,
    isError: filesError,
    error: filesErr,
  } = useQuery({
    queryKey: [
      "archive-files",
      jobId,
      appliedFilters.deviceId,
      appliedFilters.source,
      appliedFilters.from,
      appliedFilters.to,
    ],
    queryFn: () =>
      fetchArchiveFiles({
        jobId,
        deviceId: appliedFilters.deviceId || undefined,
        source: appliedFilters.source || undefined,
        from: appliedFilters.from || undefined,
        to: appliedFilters.to || undefined,
      }),
    enabled: !!jobId && !!job,
  });

  const deviceOptions: Option[] = useMemo(
    () =>
      (job?.devices ?? []).map((d) => ({
        value: d.id,
        label: d.identifier || d.id,
      })),
    [job]
  );

  const sourceOptions: Option[] = useMemo(() => {
    const available =
      job?.sources && job.sources.length
        ? (job.sources.filter((s) =>
            ALL_SOURCES.includes(s as ArchiveSource)
          ) as ArchiveSource[])
        : ALL_SOURCES;
    return available.map((s) => ({ value: s, label: t(`sources.${s}`) }));
  }, [job, t]);

  const deviceIssues = useMemo(
    () =>
      (job?.devices ?? [])
        .map((d) => {
          const drift = Object.values(d.driftDocs ?? {});
          return {
            id: d.id,
            label: d.identifier || d.id,
            status: d.status,
            error: d.error ?? null,
            missing: drift.reduce((acc, e) => acc + (e.missing || 0), 0),
            driftDocs: drift.length,
          };
        })
        .filter((d) => d.status === "failed" || d.missing > 0),
    [job]
  );

  const formatOptions: Option[] = useMemo(
    () => [
      { value: "excel", label: t("detail.export.formatExcel") },
      { value: "pdf", label: t("detail.export.formatPdf") },
    ],
    [t]
  );

  const rangeLabel =
    appliedFilters.from && appliedFilters.to
      ? `${appliedFilters.from} → ${appliedFilters.to}`
      : appliedFilters.from
        ? t("detail.export.rangeFrom", { from: appliedFilters.from })
        : appliedFilters.to
          ? t("detail.export.rangeTo", { to: appliedFilters.to })
          : t("detail.export.rangeAll");

  const showDiagnostics =
    deviceIssues.length > 0 || (job?.deferredDocs ?? 0) > 0;

  const rows = files ?? [];

  const emptyFilters = { deviceId: "", source: "" as ArchiveSource | "", from: "", to: "" };

  function applyFilters() {
    setAppliedFilters({ deviceId, source, from, to });
  }

  function clearFilters() {
    setDeviceId("");
    setSource("");
    setFrom("");
    setTo("");
    setAppliedFilters(emptyFilters);
  }

  async function copyLink(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      Swal.fire({
        icon: "success",
        title: t("detail.copied"),
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: "top-end",
      });
    } catch {
      Swal.fire({
        icon: "error",
        title: t("detail.copyError"),
        timer: 1800,
        showConfirmButton: false,
        toast: true,
        position: "top-end",
      });
    }
  }

  async function handleDelete(scopedDeviceId?: string) {
    const ok = await confirmStrongDelete({
      title: t("delete.title"),
      text: scopedDeviceId ? t("delete.textDevice") : t("delete.textJob"),
      prompt: t("delete.prompt", { word: t("delete.word") }),
      word: t("delete.word"),
      mismatch: t("delete.mismatch"),
      confirm: t("delete.confirm"),
      cancel: t("delete.cancel"),
    });
    if (!ok) return;
    Swal.fire({
      title: t("delete.deleting"),
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });
    try {
      await deleteArchiveBackup(jobId, scopedDeviceId);
      await queryClient.invalidateQueries({ queryKey: ["archive-jobs"] });
      await queryClient.invalidateQueries({ queryKey: ["archive-job", jobId] });
      await queryClient.invalidateQueries({ queryKey: ["archive-files", jobId] });
      Swal.fire({
        icon: "success",
        title: t("delete.done"),
        timer: 1800,
        showConfirmButton: false,
      });
      if (!scopedDeviceId) navigate("/respaldos");
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("delete.error"),
        text: (err as Error)?.message,
        confirmButtonColor: "#dc2626",
      });
    }
  }

  async function handleRetry() {
    const confirm = await Swal.fire({
      icon: "question",
      title: t("retry.confirmTitle"),
      text: t("retry.confirmText"),
      showCancelButton: true,
      confirmButtonText: t("retry.confirm"),
      cancelButtonText: t("retry.cancel"),
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#6b7280",
    });
    if (!confirm.isConfirmed) return;
    Swal.fire({
      title: t("retry.running"),
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });
    try {
      await retryArchiveJob(jobId);
      await queryClient.invalidateQueries({ queryKey: ["archive-jobs"] });
      await queryClient.invalidateQueries({ queryKey: ["archive-job", jobId] });
      Swal.fire({
        icon: "success",
        title: t("retry.done"),
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("retry.error"),
        text: (err as Error)?.message,
        confirmButtonColor: "#dc2626",
      });
    }
  }

  async function handleExportRange() {
    setExporting(true);
    try {
      const company = job?.companyName || job?.companyId || "respaldo";
      const ext = exportFormat === "pdf" ? "pdf" : "xlsx";
      const { deviceId: dev, source: src, from: since, to: until } = appliedFilters;
      const name = `informe_${company.replace(/[^A-Za-z0-9_-]+/g, "_")}_${since || "inicio"}_${until || "fin"}.${ext}`;
      await downloadArchiveRange(
        {
          jobId,
          deviceId: dev || undefined,
          source: (src || undefined) as ArchiveSource | undefined,
          from: since || undefined,
          to: until || undefined,
        },
        name,
        exportFormat
      );
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: t("detail.export.errTitle"),
        text: await archiveDownloadErrorText(err),
        confirmButtonText: t("detail.export.errOk"),
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setExporting(false);
    }
  }

  const deviceLabel = (id: string) =>
    deviceOptions.find((o) => o.value === id)?.label || id;

  const columns: SgColumn[] = [
    {
      header: t("detail.cols.device"),
      key: "deviceId",
      render: (f: ArchiveFileEntry) => (
        <span className="font-medium">{deviceLabel(f.deviceId)}</span>
      ),
    },
    {
      header: t("detail.cols.source"),
      key: "source",
      render: (f: ArchiveFileEntry) => <span>{t(`sources.${f.source}`)}</span>,
    },
    {
      header: t("detail.cols.day"),
      key: "day",
      render: (f: ArchiveFileEntry) => <span>{f.day}</span>,
    },
    {
      header: t("detail.cols.actions"),
      key: "actions",
      align: "right",
      render: (f: ArchiveFileEntry) => (
        <div className="flex items-center justify-end gap-2">
          <a
            href={f.url}
            target="_blank"
            rel="noreferrer"
            className="text-sm px-3 py-1.5 rounded-lg font-medium bg-primary-500 hover:bg-primary-400 text-white transition-colors whitespace-nowrap inline-flex items-center gap-1.5"
          >
            <i className="ri-download-2-line"></i>
            {t("detail.download")}
          </a>
          <button
            onClick={() => copyLink(f.url)}
            className={`text-sm px-2.5 py-1.5 rounded-lg font-medium border transition-colors ${
              isDarkMode
                ? "border-dark-border text-gray-200 hover:bg-white/5"
                : "border-gray-200 text-gray-700 hover:bg-gray-50"
            }`}
            title={t("detail.share")}
          >
            <i className="ri-share-line"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="h-full min-h-0 w-full flex flex-col">
      <div
        className={`px-6 pt-6 pb-5 border-b ${
          isDarkMode ? "border-dark-border" : "border-gray-200"
        }`}
      >
        <button
          onClick={() => navigate("/respaldos")}
          className={`text-sm mb-3 inline-flex items-center gap-1.5 ${themeClasses.textSecondary} hover:opacity-80`}
        >
          <i className="ri-arrow-left-line"></i>
          {t("detail.back")}
        </button>

        {jobError ? (
          <p className="text-red-500 text-sm">
            {(jobErr as Error)?.message || t("jobs.loadError")}
          </p>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
            <div>
              <h1 className={`text-2xl font-bold ${themeClasses.text}`}>
                {t("detail.title", {
                  company: job?.companyName || job?.companyId || "—",
                })}
              </h1>
              {job && (
                <div className="flex items-center gap-2 mt-2">
                  <ReasonBadge
                    reason={job.reason}
                    label={t(`reason.${job.reason}`)}
                    isDarkMode={isDarkMode}
                  />
                  <StatusBadge
                    status={job.status}
                    label={t(`status.${job.status}`)}
                    isDarkMode={isDarkMode}
                  />
                  <span className={`text-sm ${themeClasses.textSecondary}`}>
                    {job.doneCount}/{job.deviceCount}
                  </span>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {job?.status === "failed" && (
                <button
                  onClick={handleRetry}
                  className="text-sm px-4 py-2.5 rounded-lg font-medium bg-primary-500 hover:bg-primary-400 text-white transition-colors whitespace-nowrap inline-flex items-center gap-2"
                >
                  <i className="ri-refresh-line"></i>
                  {t("retry.button")}
                </button>
              )}
              <button
                onClick={() => handleDelete(deviceId || undefined)}
                disabled={job?.status === "deleted"}
                className={`text-sm px-4 py-2.5 rounded-lg font-medium border transition-colors whitespace-nowrap inline-flex items-center gap-2 ${
                  isDarkMode
                    ? "border-dark-border text-red-400 hover:bg-white/5"
                    : "border-gray-200 text-red-600 hover:bg-gray-50"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                <i className="ri-delete-bin-line"></i>
                {deviceId ? t("detail.deleteDevice") : t("delete.title")}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6 space-y-5">
        <div
          className={`rounded-xl border p-4 ${
            isDarkMode
              ? "border-dark-border bg-dark-surface"
              : "border-gray-200 bg-white"
          }`}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="lg:col-span-2">
              <label className={`block text-xs mb-1 ${themeClasses.textSecondary}`}>
                {t("detail.device")}
              </label>
              <Select
                isClearable
                options={deviceOptions}
                value={deviceOptions.find((o) => o.value === deviceId) ?? null}
                onChange={(opt) => setDeviceId((opt as Option | null)?.value ?? "")}
                placeholder={t("detail.allDevices")}
                styles={selectStyles}
                menuPortalTarget={document.body}
              />
            </div>
            <div>
              <label className={`block text-xs mb-1 ${themeClasses.textSecondary}`}>
                {t("detail.source")}
              </label>
              <Select
                isClearable
                options={sourceOptions}
                value={sourceOptions.find((o) => o.value === source) ?? null}
                onChange={(opt) =>
                  setSource(((opt as Option | null)?.value as ArchiveSource) ?? "")
                }
                placeholder={t("detail.allSources")}
                styles={selectStyles}
                menuPortalTarget={document.body}
              />
            </div>
            <div>
              <label className={`block text-xs mb-1 ${themeClasses.textSecondary}`}>
                {t("detail.from")}
              </label>
              <input
                type="date"
                value={from}
                max={to || undefined}
                onChange={(e) => setFrom(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  isDarkMode
                    ? "bg-dark-card border-dark-border text-white"
                    : "bg-gray-50 border-gray-200 text-gray-900"
                }`}
              />
            </div>
            <div>
              <label className={`block text-xs mb-1 ${themeClasses.textSecondary}`}>
                {t("detail.to")}
              </label>
              <input
                type="date"
                value={to}
                min={from || undefined}
                onChange={(e) => setTo(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  isDarkMode
                    ? "bg-dark-card border-dark-border text-white"
                    : "bg-gray-50 border-gray-200 text-gray-900"
                }`}
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={applyFilters}
              className="text-sm px-4 py-2 rounded-lg font-medium bg-primary-500 hover:bg-primary-400 text-white transition-colors inline-flex items-center gap-2"
            >
              <i className="ri-search-line"></i>
              {filesFetching ? t("detail.searching") : t("detail.search")}
            </button>
            <button
              onClick={clearFilters}
              className={`text-sm px-4 py-2 rounded-lg font-medium border transition-colors ${
                isDarkMode
                  ? "border-dark-border text-gray-200 hover:bg-white/5"
                  : "border-gray-200 text-gray-700 hover:bg-gray-50"
              }`}
            >
              {t("detail.clear")}
            </button>
          </div>

          <div
            className={`mt-4 pt-4 border-t ${
              isDarkMode ? "border-dark-border" : "border-gray-200"
            }`}
          >
            <h3 className={`text-sm font-semibold mb-2 ${themeClasses.text}`}>
              {t("detail.export.title")}
            </h3>
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="w-full sm:w-52">
              <label className={`block text-xs mb-1 ${themeClasses.textSecondary}`}>
                {t("detail.export.format")}
              </label>
              <Select
                options={formatOptions}
                value={formatOptions.find((o) => o.value === exportFormat) ?? null}
                onChange={(opt) =>
                  setExportFormat(
                    ((opt as Option | null)?.value ?? "excel") as ArchiveExportFormat
                  )
                }
                styles={selectStyles}
                menuPortalTarget={document.body}
              />
            </div>
            <button
              onClick={handleExportRange}
              disabled={exporting || rows.length === 0}
              className="text-sm px-4 py-2 rounded-lg font-medium bg-emerald-600 hover:bg-emerald-500 text-white transition-colors inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <i
                className={
                  exportFormat === "pdf" ? "ri-file-pdf-2-line" : "ri-file-excel-2-line"
                }
              ></i>
              {exporting ? t("detail.export.working") : t("detail.export.button")}
            </button>
            <div className="flex flex-wrap items-center gap-2 sm:pb-2">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                  isDarkMode
                    ? "bg-white/10 text-gray-200"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                <i className="ri-file-list-3-line"></i>
                {t("detail.export.badgeFiles", {
                  n: rows.length.toLocaleString(i18n.language),
                })}
              </span>
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                  isDarkMode
                    ? "bg-white/10 text-gray-200"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                <i className="ri-calendar-line"></i>
                {rangeLabel}
              </span>
            </div>
            </div>
          </div>
        </div>

        {showDiagnostics && (
          <div
            className={`rounded-xl border p-4 ${
              isDarkMode
                ? "border-dark-border bg-dark-surface"
                : "border-gray-200 bg-white"
            }`}
          >
            <h2 className={`text-lg font-semibold mb-1 ${themeClasses.text}`}>
              {t("detail.diagnostics.title")}
            </h2>
            {(job?.driftMissing ?? 0) > 0 && (
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {t("detail.diagnostics.drift", {
                  missing: (job?.driftMissing ?? 0).toLocaleString(i18n.language),
                  devices: job?.driftDeviceCount ?? 0,
                })}
              </p>
            )}
            {(job?.deferredDocs ?? 0) > 0 && (
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {t("detail.diagnostics.deferred", { n: job?.deferredDocs ?? 0 })}
              </p>
            )}
            {(job?.duplicatesDropped ?? 0) > 0 && (
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {t("detail.diagnostics.duplicates", {
                  n: (job?.duplicatesDropped ?? 0).toLocaleString(i18n.language),
                })}
              </p>
            )}
            {deviceIssues.length > 0 && (
              <ul className="mt-3 space-y-2">
                {deviceIssues.map((d) => (
                  <li
                    key={d.id}
                    className={`text-sm flex flex-col sm:flex-row sm:items-center sm:gap-2 ${themeClasses.textSecondary}`}
                  >
                    <span className={`font-medium ${themeClasses.text}`}>
                      {d.label}
                    </span>
                    {d.status === "failed" && d.error && (
                      <span className="text-red-500">{d.error}</span>
                    )}
                    {d.missing > 0 && (
                      <span>
                        {t("detail.diagnostics.deviceDrift", {
                          missing: d.missing.toLocaleString(i18n.language),
                          docs: d.driftDocs,
                        })}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="flex items-center justify-between">
          <h2 className={`text-lg font-semibold ${themeClasses.text}`}>
            {t("detail.filesTitle")}
          </h2>
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("detail.filesCount", {
              n: rows.length.toLocaleString(i18n.language),
            })}
          </span>
        </div>

        {filesError ? (
          <p className="text-red-500 text-sm">
            {(filesErr as Error)?.message || t("detail.loadError")}
          </p>
        ) : (
          <SgTable
            columns={columns}
            rows={rows}
            isDarkMode={isDarkMode}
            loading={jobLoading || filesLoading}
            emptyText={t("detail.empty")}
            rowKey={(f: ArchiveFileEntry) => f.path}
          />
        )}
      </div>
    </div>
  );
}
